import { useState, useRef, useEffect } from 'react';

const GRID_TYPES = [
  { id: 'square', label: 'Square' },
  { id: 'dot', label: 'Dot Grid' },
  { id: 'isometric', label: 'Isometric' },
  { id: 'hexagonal', label: 'Hexagonal' },
  { id: 'polar', label: 'Polar' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'lined', label: 'Lined' },
];

const PAGE_SIZES = [
  { id: 'letter', label: 'Letter', width: 816, height: 1056 },
  { id: 'a4', label: 'A4', width: 794, height: 1123 },
  { id: '11x17', label: '11×17', width: 1056, height: 1632 },
  { id: 'legal', label: 'Legal', width: 816, height: 1344 },
  { id: 'a3', label: 'A3', width: 1123, height: 1587 },
  { id: 'a2', label: 'A2', width: 1587, height: 2245 },
  { id: 'poster', label: 'Poster', width: 2304, height: 3456 },
  { id: 'movie-poster', label: 'Movie Poster', width: 2592, height: 3840 },
];

const SPACING_OPTIONS = [
  { value: 2, label: '2 mm' },
  { value: 3, label: '3 mm' },
  { value: 5, label: '5 mm' },
  { value: 6.35, label: '¼″' },
  { value: 7.5, label: '7.5 mm' },
  { value: 10, label: '1 cm' },
  { value: 12.7, label: '½″' },
  { value: 25.4, label: '1″' },
];

const LINE_COLORS = [
  { value: '#1a1a1a', label: 'Black' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#ef4444', label: 'Red' },
  { value: '#22c55e', label: 'Green' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#6b7280', label: 'Gray' },
  { value: '#d1d5db', label: 'Light Gray' },
];

export default function Controls({ settings, onChange, onPrint, onDownload }) {
  const [downloadOpen, setDownloadOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDownloadOpen(false);
      }
    }
    if (downloadOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [downloadOpen]);

  return (
    <div className="controls">
      <div className="controls-inner">
        <div className="controls-header">
          <h2>Settings</h2>
        </div>

        <div className="controls-section">
          <label className="control-label">Grid Type</label>
          <div className="grid-type-grid">
            {GRID_TYPES.map((gt) => (
              <button
                key={gt.id}
                className={`grid-type-btn ${settings.type === gt.id ? 'active' : ''}`}
                onClick={() => onChange('type', gt.id)}
              >
                {gt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="controls-section">
          <label className="control-label">
            Grid Size
            <span className="control-value">{SPACING_OPTIONS.find((s) => s.value === settings.spacing)?.label}</span>
          </label>
          <div className="spacing-options">
            {SPACING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`spacing-btn ${settings.spacing === opt.value ? 'active' : ''}`}
                onClick={() => onChange('spacing', opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="controls-section">
          <label className="control-label">
            Line Color
            <span className="control-value">{LINE_COLORS.find((c) => c.value === settings.color)?.label}</span>
          </label>
          <div className="color-options">
            {LINE_COLORS.map((c) => (
              <button
                key={c.value}
                className={`color-btn ${settings.color === c.value ? 'active' : ''}`}
                style={{ backgroundColor: c.value }}
                onClick={() => onChange('color', c.value)}
                title={c.label}
              />
            ))}
          </div>
        </div>

        {(settings.type === 'square' || settings.type === 'engineering') && (
          <>
            <div className="controls-section">
              <label className="control-label">
                Small Box Line
                <span className="control-value">{settings.minorWidth.toFixed(1)} px</span>
              </label>
              <input
                type="range" min="0.1" max="1.5" step="0.1"
                value={settings.minorWidth}
                onChange={(e) => onChange('minorWidth', parseFloat(e.target.value))}
                className="slider"
              />
            </div>
            <div className="controls-section">
              <label className="control-label">
                Large Box Line
                <span className="control-value">{settings.majorWidth.toFixed(1)} px</span>
              </label>
              <input
                type="range" min="0.3" max="3" step="0.1"
                value={settings.majorWidth}
                onChange={(e) => onChange('majorWidth', parseFloat(e.target.value))}
                className="slider"
              />
            </div>
          </>
        )}

        {settings.type !== 'square' && settings.type !== 'engineering' && (
          <div className="controls-section">
            <label className="control-label">
              Line Weight
              <span className="control-value">{settings.minorWidth.toFixed(1)} px</span>
            </label>
            <input
              type="range" min="0.1" max="3" step="0.1"
              value={settings.minorWidth}
              onChange={(e) => onChange('minorWidth', parseFloat(e.target.value))}
              className="slider"
            />
          </div>
        )}

        <div className="controls-section">
          <label className="control-label">
            Page Size
            <span className="control-value">{PAGE_SIZES.find((p) => p.id === settings.pageSize)?.label}</span>
          </label>
          <div className="page-size-options">
            {PAGE_SIZES.map((ps) => (
              <button
                key={ps.id}
                className={`page-size-btn ${settings.pageSize === ps.id ? 'active' : ''}`}
                onClick={() => onChange('pageSize', ps.id)}
                title={`${(ps.width / 96).toFixed(1)}×${(ps.height / 96).toFixed(1)}″`}
              >
                {ps.label}
              </button>
            ))}
          </div>
        </div>

        <div className="controls-section">
          <label className="control-label">Orientation</label>
          <div className="orientation-options">
            <button
              className={`orientation-btn ${settings.orientation === 'portrait' ? 'active' : ''}`}
              onClick={() => onChange('orientation', 'portrait')}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="4" y="2" width="12" height="16" rx="1" />
              </svg>
              Portrait
            </button>
            <button
              className={`orientation-btn ${settings.orientation === 'landscape' ? 'active' : ''}`}
              onClick={() => onChange('orientation', 'landscape')}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="4" width="16" height="12" rx="1" />
              </svg>
              Landscape
            </button>
          </div>
        </div>

        {(settings.type === 'square' || settings.type === 'engineering') && (
          <div className="controls-section">
            <label className="control-label">
              Accent Every
              <span className="control-value">{settings.accentEvery > 0 ? `${settings.accentEvery} squares` : 'None'}</span>
            </label>
            <div className="accent-options">
              {[0, 2, 3, 4, 5, 10].map((n) => (
                <button
                  key={n}
                  className={`accent-btn ${settings.accentEvery === n ? 'active' : ''}`}
                  onClick={() => onChange('accentEvery', n)}
                >
                  {n === 0 ? 'Off' : n}
                </button>
              ))}
            </div>
          </div>
        )}

        {settings.type === 'lined' && (
          <div className="controls-section">
            <label className="control-label">
              Left Margin
              <span className="control-value">{settings.marginLeft} mm</span>
            </label>
            <input
              type="range" min="0" max="50" step="1"
              value={settings.marginLeft}
              onChange={(e) => onChange('marginLeft', parseFloat(e.target.value))}
              className="slider"
            />
          </div>
        )}

        <div className="controls-footer">
          <button className="btn btn-primary btn-print" onClick={onPrint}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 6V3h8v3" />
              <rect x="3" y="6" width="12" height="7" rx="1.5" />
              <path d="M5 10h8v5H5z" />
            </svg>
            Print
          </button>
          <div className="download-btn-wrap" ref={dropdownRef}>
            <button className="btn btn-primary btn-download" onClick={() => setDownloadOpen(!downloadOpen)}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 2v10" />
                <path d="M5 8l4 4 4-4" />
                <path d="M3 15v1h12v-1" />
              </svg>
              Download
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M2 3.5l3 3 3-3" />
              </svg>
            </button>
            {downloadOpen && (
              <div className="download-dropdown">
                <button className="dropdown-item" onClick={() => { onDownload('pdf'); setDownloadOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 2h10v4l-3 3H3z"/><path d="M3 10h10v4H3z"/></svg>
                  PDF
                </button>
                <button className="dropdown-item" onClick={() => { onDownload('png'); setDownloadOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="12" height="12" rx="1"/><circle cx="6" cy="6" r="1.5"/><path d="M2 10l3-3 2 2 3-3 4 4"/></svg>
                  PNG
                </button>
                <button className="dropdown-item" onClick={() => { onDownload('svg'); setDownloadOpen(false); }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 5l4-3 4 3"/><path d="M10 11l-4 3-4-3"/><path d="M6 2v12"/></svg>
                  SVG
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
